// src/components/billing/BillingGateListener.tsx
/**
 * Global listener for billing-related gate events dispatched by the
 * API interceptor (beta_restricted, quota_exceeded, feature_not_available).
 *
 * Mount this once — ideally inside <SnackbarProvider> — to get automatic
 * toast notifications whenever a gated action is blocked.
 * Includes an "Upgrade" action button that navigates to /billing.
 */

import React, { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Button, useTheme } from "@mui/material";
import { RocketLaunch as UpgradeIcon } from "@mui/icons-material";
import { athenaColors } from "theme/palette";

const BillingGateListener: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Debounce: avoid spamming toasts for rapid-fire failures
  const lastShown = useRef<Record<string, number>>({});
  const COOLDOWN_MS = 3000;

  const showWithUpgrade = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        variant: "warning",
        autoHideDuration: 10000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        action: (snackbarId) => (
          <Button
            size="small"
            variant="contained"
            startIcon={<UpgradeIcon />}
            onClick={() => {
              closeSnackbar(snackbarId);
              navigate("/billing");
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              backgroundColor: theme.palette.common.white,
              color: athenaColors.white,
              "&:hover": { backgroundColor: theme.palette.grey[200] },
              ml: 1,
            }}
          >
            Upgrade
          </Button>
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar, navigate, theme]
  );

  useEffect(() => {
    const handleBeta = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const now = Date.now();
      if (now - (lastShown.current["beta"] ?? 0) < COOLDOWN_MS) return;
      lastShown.current["beta"] = now;

      enqueueSnackbar(
        detail.message ||
          "This feature is available to Beta Program participants only. Contact us to request access.",
        {
          variant: "info",
          autoHideDuration: 6000,
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        }
      );
    };

    const handleBilling = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const now = Date.now();
      const key = `billing-${detail.error ?? "unknown"}`;
      if (now - (lastShown.current[key] ?? 0) < COOLDOWN_MS) return;
      lastShown.current[key] = now;

      const errCode = detail.error as string | undefined;

      if (errCode === "quota_exceeded") {
        showWithUpgrade(
          detail.message ||
            "You have reached your usage quota. Would you like to upgrade?"
        );
      } else if (errCode === "feature_not_available") {
        showWithUpgrade(
          detail.message ||
            "This feature requires a plan upgrade. Would you like to upgrade?"
        );
      } else if (errCode === "subscription_inactive") {
        showWithUpgrade(
          detail.message ||
            "Your subscription is not active. Please update your billing."
        );
      } else {
        showWithUpgrade(
          detail.message || "This action requires a plan upgrade. Would you like to upgrade?"
        );
      }
    };

    window.addEventListener("beta-restricted", handleBeta);
    window.addEventListener("billing-gate", handleBilling);

    return () => {
      window.removeEventListener("beta-restricted", handleBeta);
      window.removeEventListener("billing-gate", handleBilling);
    };
  }, [enqueueSnackbar, showWithUpgrade]);

  return null; // Render nothing — purely an event listener
};

export default BillingGateListener;
